import { useRef, useState } from 'react';

import { AlertTriangle, ArrowLeft, FileText, Loader2, Sparkles, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { ModelSelect } from '@/components/service-history/ModelSelect';
import { ServiceFactsView } from '@/components/service-history/ServiceFactsView';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReadServiceFiles, useSaveServiceDraft } from '@/hooks/useServiceHistory';
import { useAuthStore } from '@/stores/auth.store';

import {
  SERVICE_HISTORY_DEFAULT_MODEL,
  SERVICE_HISTORY_MAX_FILES,
  SERVICE_HISTORY_MAX_FILE_BYTES,
} from '@repo/shared-types';

import type { ServiceDraft, ServiceHistoryModel } from '@repo/shared-types';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*';

const formatSize = (bytes: number) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

/**
 * Upload a car's service files. "Read files" runs the AI once and fills in the
 * registration; "Save" stores the summary against that registration.
 */
export const ServiceHistoryUploadPage = () => {
  usePageTitle('Upload Service History');
  const navigate = useNavigate();
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const inputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [model, setModel] = useState<ServiceHistoryModel>(SERVICE_HISTORY_DEFAULT_MODEL);
  const [draft, setDraft] = useState<ServiceDraft | null>(null);
  const [registration, setRegistration] = useState('');
  const [dragging, setDragging] = useState(false);

  const readFiles = useReadServiceFiles();
  const saveDraft = useSaveServiceDraft();

  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground text-sm">
          Service history is restricted to administrators.
        </p>
      </div>
    );
  }

  const tooLarge = files.filter((file) => file.size > SERVICE_HISTORY_MAX_FILE_BYTES);
  const tooMany = files.length > SERVICE_HISTORY_MAX_FILES;

  const addFiles = (incoming: FileList | null) => {
    if (!incoming?.length) return;
    setFiles((current) => [...current, ...Array.from(incoming)]);
    // New files mean the old reading no longer matches.
    setDraft(null);
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
    setDraft(null);
  };

  const handleRead = async () => {
    const result = await readFiles.mutateAsync({ files, model });
    setDraft(result);
    setRegistration(result.suggested_registration ?? '');
  };

  const handleSave = async () => {
    if (!draft) return;
    const vehicle = await saveDraft.mutateAsync({
      draftId: draft.draft_id,
      registration: registration.trim(),
    });
    navigate(`/service-history/${vehicle.id}`);
  };

  const busy = readFiles.isPending || saveDraft.isPending;

  return (
    <div className="max-w-5xl space-y-4 p-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/service-history">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Service History
        </Link>
      </Button>
      <h1 className="text-2xl font-semibold">Upload service history</h1>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">1. Add files</CardTitle>
          <CardDescription>
            PDFs and photos of service book pages, invoices or MOT certificates for one car. Up to{' '}
            {SERVICE_HISTORY_MAX_FILES} files, 20 MB each.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <button
            type="button"
            className={`w-full rounded-md border-2 border-dashed p-6 text-center text-sm transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(event.dataTransfer.files);
            }}
            disabled={busy}
          >
            Drop files here or click to choose
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
          />

          {files.length > 0 && (
            <ul className="divide-y rounded-md border">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{file.name}</span>
                  <span
                    className={`text-xs ${
                      file.size > SERVICE_HISTORY_MAX_FILE_BYTES
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {formatSize(file.size)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(index)}
                    disabled={busy}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {tooLarge.length > 0 && (
            <p className="text-destructive text-sm">
              Over 20 MB: {tooLarge.map((file) => file.name).join(', ')}
            </p>
          )}
          {tooMany && (
            <p className="text-destructive text-sm">
              Too many files. Remove some (max {SERVICE_HISTORY_MAX_FILES}).
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <ModelSelect value={model} onChange={setModel} disabled={busy} />
            <Button
              onClick={handleRead}
              disabled={!files.length || tooLarge.length > 0 || tooMany || busy}
            >
              {readFiles.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {readFiles.isPending ? 'Reading files…' : 'Read files'}
            </Button>
          </div>
          {readFiles.isPending && (
            <p className="text-muted-foreground text-xs">
              This can take a minute or two for many pages.
            </p>
          )}
        </CardContent>
      </Card>

      {draft && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">2. Check and save</CardTitle>
            <CardDescription>
              Read by {draft.summary.model_name}
              {draft.summary.input_tokens !== null &&
                ` · ${draft.summary.input_tokens.toLocaleString('en-GB')} input / ${(
                  draft.summary.output_tokens ?? 0
                ).toLocaleString('en-GB')} output tokens`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="registration" className="text-sm font-medium">
                Registration
              </label>
              <Input
                id="registration"
                value={registration}
                onChange={(event) => setRegistration(event.target.value.toUpperCase())}
                placeholder="AB12 CDE"
                className="max-w-xs font-mono"
              />
              {draft.registration_warning && (
                <p className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  {draft.registration_warning}
                </p>
              )}
              {draft.existing_vehicle_id &&
                registration.replace(/\s/g, '') === draft.suggested_registration && (
                  <p className="text-muted-foreground text-sm">
                    This car already has saved files. Saving adds these files and rebuilds the
                    summary from all of them.
                  </p>
                )}
            </div>

            <ServiceFactsView summary={draft.summary} />

            <Button onClick={handleSave} disabled={registration.trim().length < 2 || busy}>
              {saveDraft.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
